import { useState } from 'react';
import type { FocusEvent, ReactNode } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Field from './Field';
import { borderLabel, useLabelPlacement } from './labelPlacement';
import { describedBy, inputAriaLabel, inputAttrProps, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Single-line text, and (with `multiline`, or the TextArea export) multi-line,
 * with AppTextBox's props.
 *
 * onValueChanged fires on each keystroke, or with `commitOnBlur` only when the
 * field is left (or Enter in a single-line field). It never fires because the
 * `value` prop changed.
 */
export interface TextInputProps extends BaseFieldProps {
  value: string | null | undefined;
  /** The text. User changes only. */
  onValueChanged: (value: string) => void;
  /** Input type, as AppTextBox `mode`. */
  mode?: 'text' | 'password' | 'email' | 'tel' | 'url' | 'search';
  maxLength?: number;
  /** Report only when the field is left (or Enter, single-line). */
  commitOnBlur?: boolean;
  /** Clear button while there is text. */
  showClearButton?: boolean;
  /** Focus left the field. The target is the input. */
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Multi-line. Prefer the TextArea export. */
  multiline?: boolean;
  /** Visible lines when multi-line. Default 3. */
  rows?: number;
  /** Controls beside the label (an info icon, a copy button). See Field. */
  labelAdornment?: ReactNode;
  /** Extra controls inside the field at the right, after the clear button
   *  (e.g. a "pick from saved messages" button). */
  endAdornment?: ReactNode;
}

export default function TextInput(props: TextInputProps) {
  const {
    value, onValueChanged, mode = 'text', maxLength, commitOnBlur = false, showClearButton = false, onBlur,
    multiline = false, rows = 3, label, ariaLabel, placeholder, required, disabled, readOnly, helperText, errorText,
    width, height, className, inputAttr, labelPlacement, labelAdornment, endAdornment,
  } = props;
  const ids = useFieldIds(inputAttr);
  const placement = useLabelPlacement(labelPlacement);
  const outlineLabel = borderLabel(placement, label, required);
  const current = value ?? '';
  // commitOnBlur: the typed text, tied to the value it was typed over (see DateField).
  const [draft, setDraft] = useState<{ text: string; forValue: string } | null>(null);
  const live = draft && draft.forValue === current ? draft : null;
  const text = live ? live.text : current;

  const commit = () => {
    if (!live) return;
    setDraft(null);
    if (live.text !== current) onValueChanged(live.text);
  };

  const invalid = isInvalid(props);
  const editable = !disabled && !readOnly;

  return (
    <Field
      inputId={ids.id}
      label={label}
      required={required}
      helperText={helperText}
      helperId={ids.helperId}
      errorText={errorText}
      errorId={ids.errorId}
      error={invalid}
      disabled={disabled}
      width={width}
      height={height}
      labelPlacement={placement}
      labelAdornment={labelAdornment}
      className={`${readOnly ? 'app-field--readonly ' : ''}${className ?? ''}`.trim() || undefined}
    >
      <TextField
        id={ids.id}
        type={multiline ? undefined : mode}
        value={text}
        onChange={(e) => {
          if (commitOnBlur) setDraft({ text: e.target.value, forValue: current });
          else onValueChanged(e.target.value);
        }}
        onBlur={(e) => { commit(); onBlur?.(e); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !multiline) commit(); }}
        placeholder={placeholder}
        disabled={disabled}
        error={invalid}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        size="small"
        fullWidth
        label={outlineLabel}
        InputLabelProps={outlineLabel ? { shrink: true } : undefined}
        InputProps={{
          notched: !!outlineLabel,
          readOnly,
          endAdornment: (showClearButton && editable && text) || endAdornment ? (
            <InputAdornment position="end">
              {showClearButton && editable && text ? (
                <IconButton size="small" edge="end" aria-label={`Clear ${label ?? ariaLabel ?? 'text'}`} onClick={() => { setDraft(null); onValueChanged(''); }}>
                  <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 12 }} />
                </IconButton>
              ) : null}
              {endAdornment}
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{
          ...inputAttrProps(inputAttr),
          maxLength,
          'aria-label': inputAriaLabel(label, required, ariaLabel),
          'aria-required': required || undefined,
          'aria-invalid': invalid || undefined,
          'aria-describedby': describedBy(ids, { helperText, errorText }),
        }}
      />
    </Field>
  );
}
